import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PaginatedResponse } from '../api';
import {
  blockMember,
  CompanionStatusType,
  MemberInfo,
  unblockMember,
} from '../profile';
import {
  acceptCompanionRequest,
  fetchCompanions,
  rejectCompanionRequest,
  removeCompanion,
  fetchCompanionsInTheRoom,
  fetchCompanionsInTheRoomCount,
} from './actions';

export interface CompanionsState {
  companions: PaginatedResponse<MemberInfo[]>;
  chatDrawerCompanions: PaginatedResponse<MemberInfo[]>;
  loadingCompanions: boolean;
  loadingCompanionRequestByCompanionId: {
    [companionId: string]: boolean;
  };
  loadingAcceptingCompanionRequest: boolean;
  loadingRejectingCompanionRequest: boolean;
  loadingCompanionsWidget: boolean;
  numberOfCompanionsInTheRoom: number;
}

const initialState: CompanionsState = {
  companions: { data: [] },
  chatDrawerCompanions: { data: [] },
  loadingCompanions: false,
  loadingAcceptingCompanionRequest: false,
  loadingRejectingCompanionRequest: false,
  loadingCompanionRequestByCompanionId: {},
  loadingCompanionsWidget: false,
  numberOfCompanionsInTheRoom: 0,
};

const removeCompanionFromState = (state: CompanionsState, id: string) => {
  state.companions.data = state.companions.data.filter(
    (profile) => profile.memberId !== id
  );
};

const companionsSlice = createSlice({
  name: 'companions',
  initialState,
  reducers: {
    removeCompanionById: (state, action: PayloadAction<string>) => {
      removeCompanionFromState(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanions.pending, (state, action) => {
        state.loadingCompanions = true;
        if (!action.meta.arg.xMsContinuationToken) {
          state.loadingCompanionsWidget = true;
        }
      })
      .addCase(fetchCompanions.fulfilled, (state, action) => {
        if (action.meta.arg.isForChatDrawer) {
          state.chatDrawerCompanions = {
            data: action.payload.data || [],
            xMsContinuationToken: action.payload?.xMsContinuationToken,
          };
        } else {
          state.companions = {
            data: action.payload.data || [],
            xMsContinuationToken: action.payload?.xMsContinuationToken,
          };
        }
        state.loadingCompanions = false;
        state.loadingCompanionsWidget = false;
      })
      .addCase(acceptCompanionRequest.pending, (state, action) => {
        state.loadingCompanionRequestByCompanionId[
          action.meta.arg.companionId
        ] = true;
        state.loadingAcceptingCompanionRequest = true;
      })
      .addCase(acceptCompanionRequest.rejected, (state, action) => {
        state.loadingCompanionRequestByCompanionId[
          action.meta.arg.companionId
        ] = false;
        state.loadingAcceptingCompanionRequest = false;
      })
      .addCase(acceptCompanionRequest.fulfilled, (state, action) => {
        if (action.meta.arg.memberInfo) {
          state.companions = {
            ...state.companions,
            data: [
              ...state.companions.data,
              {
                ...action.meta.arg.memberInfo,
                isCompanion: true,
                companionStatus: CompanionStatusType.active,
              },
            ],
          };
        }
        state.loadingCompanionRequestByCompanionId[
          action.meta.arg.companionId
        ] = false;
        state.loadingAcceptingCompanionRequest = false;
      })
      .addCase(rejectCompanionRequest.pending, (state, action) => {
        state.loadingCompanionRequestByCompanionId[
          action.meta.arg.companionId
        ] = true;
        state.loadingRejectingCompanionRequest = true;
      })
      .addCase(rejectCompanionRequest.rejected, (state, action) => {
        state.loadingCompanionRequestByCompanionId[
          action.meta.arg.companionId
        ] = false;
        state.loadingRejectingCompanionRequest = false;
      })
      .addCase(rejectCompanionRequest.fulfilled, (state, action) => {
        state.loadingCompanionRequestByCompanionId[
          action.meta.arg.companionId
        ] = false;
        state.loadingRejectingCompanionRequest = false;
      })
      .addCase(removeCompanion.fulfilled, (state, action) => {
        removeCompanionFromState(state, action.meta.arg.companionId);
      })
      .addCase(blockMember.fulfilled, (state, action) => {
        state.companions.data = [
          ...state.companions.data.map((companion) =>
            companion.memberId !== action.meta.arg.memberId
              ? companion
              : { ...companion, isBlockedMember: true }
          ),
        ];
      })
      .addCase(unblockMember.fulfilled, (state, action) => {
        state.companions.data = [
          ...state.companions.data.map((companion) =>
            companion.memberId !== action.meta.arg.memberId
              ? companion
              : { ...companion, isBlockedMember: false }
          ),
        ];
      })
      .addCase(fetchCompanionsInTheRoom.pending, (state) => {
        state.loadingCompanions = true;
      })
      .addCase(fetchCompanionsInTheRoom.fulfilled, (state, action) => {
        state.loadingCompanions = false;
        if (!action.meta.arg.xMsContinuationToken) {
          state.loadingCompanionsWidget = false;
        }
        let companions = state.companions.data || [];
        if (action.meta.arg.xMsContinuationToken) {
          companions.push(...action.payload?.data);
        } else if (action.payload?.data) {
          companions = [...action.payload?.data];
        }
        state.companions = {
          data: companions,
          xMsContinuationToken: action.payload?.xMsContinuationToken,
        };
      })
      .addCase(fetchCompanionsInTheRoom.rejected, (state) => {
        state.loadingCompanions = false;
      })
      .addCase(fetchCompanionsInTheRoomCount.pending, (state) => {
        state.loadingCompanions = true;
      })
      .addCase(fetchCompanionsInTheRoomCount.fulfilled, (state, action) => {
        state.loadingCompanions = false;
        state.numberOfCompanionsInTheRoom = action.payload;
      })
      .addCase(fetchCompanionsInTheRoomCount.rejected, (state) => {
        state.loadingCompanions = false;
      });
  },
});

export const { removeCompanionById } = companionsSlice.actions;

export default companionsSlice.reducer;
